<script setup lang="ts">
/**
 * One quiz step: Head (title + subtitle), Content, Footer Back / Continue.
 * Desktop — Card 640 centred in the Stage; mobile — the footer sits at the bottom.
 */
import { FairButton } from '@/ui/primitives/FairButton'

interface FairQuizStepProps {
  title: string
  subtitle?: string
  showBack?: boolean
  showFooter?: boolean
  backLabel?: string
  continueLabel?: string
  continueDisabled?: boolean
  continueLoading?: boolean
  /** Content grows and scrolls (lists, grids); the footer stays in view. */
  fill?: boolean
}

withDefaults(defineProps<FairQuizStepProps>(), {
  subtitle: '',
  showBack: true,
  showFooter: true,
  backLabel: 'Back',
  continueLabel: 'Continue',
  continueDisabled: false,
  continueLoading: false,
  fill: false,
})

const emit = defineEmits<{
  back: []
  continue: []
}>()
</script>

<template>
  <div
    class="fair-quiz-stage"
    :class="{ 'fair-quiz-stage--fill': fill }"
  >
    <section class="fair-quiz-card">
      <div class="fair-quiz-card__head">
        <slot name="eyebrow" />
        <h2 class="fair-quiz-card__title">
          {{ title }}
        </h2>
        <p
          v-if="subtitle"
          class="fair-quiz-card__subtitle"
        >
          {{ subtitle }}
        </p>
        <slot name="head" />
      </div>

      <div class="fair-quiz-card__content">
        <slot />
      </div>

      <div
        v-if="showFooter"
        class="fair-quiz-card__footer"
      >
        <slot name="footer">
          <FairButton
            v-if="showBack"
            class="fair-quiz-card__back"
            variant="tonal"
            size="auto"
            :label="backLabel"
            @click="emit('back')"
          />
          <FairButton
            class="fair-quiz-card__continue"
            variant="primary"
            size="auto"
            :label="continueLabel"
            :disabled="continueDisabled"
            :loading="continueLoading"
            @click="emit('continue')"
          />
        </slot>
      </div>
    </section>
  </div>
</template>
